import 'bootstrap/dist/css/bootstrap.css';
import { useState, useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload, faTimesCircle, faDownload, faRotate,
  faChevronLeft, faChevronRight, faRotateLeft, faFile
} from '@fortawesome/free-solid-svg-icons';
import CustomAlert from '../../../shared/customAlert';
import Spinner from '../../../shared/spinner';
import RemessasService from '../../../services/importacaoAquisicaoService';
import PromotoraService from '../../../services/promotoraService';

// Tipos
interface Remessa {
  id: number;
  uuid: string;
  codigo: string;
  arquivo: string;
  nomeArquivo: string;
  layout: string;
  status: 'FINALIZADO' | 'ERRO' | 'PENDENTE' | 'PROCESSANDO' | 'DESFEITO';
  usuario: string;
  dtaCriacao: string;
  dtaProcessamento?: string;
  dtaFinalizacao?: string;
  contratosSucesso?: string;
  contratosErro?: string;
  totalContratos?: string;
}

interface LogOutroStatus {
  id: number;
  numeroContrato: string;
  status: string;
  descricao: string;
  dtaProcessamento?: string;
}

interface Layout {
  id: string;
  nome: string;
}

interface Alert {
  message: string;
  type: 'success' | 'error';
}

interface Promotora {
  id: string;
  nome: string;
}

// Componente
function RemessasComponent() {
  const [alert, setAlert] = useState<Alert | null>(null);
  const [loading, setLoading] = useState(false);
  const [remessas, setRemessas] = useState<Remessa[]>([]);
  const [modalUploadAberto, setModalUploadAberto] = useState(false);
  const [modalLogsAberto, setModalLogsAberto] = useState(false);
  const [logsOutrosStatus, setLogsOutrosStatus] = useState<LogOutroStatus[]>([]);
  const [remessaSelecionada, setRemessaSelecionada] = useState<Remessa | null>(null);
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  const [layoutSelecionado, setLayoutSelecionado] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [campoDescritivo, setCampoDescritivo] = useState('');

  const remessasService = new RemessasService();
  const promotoraService = new PromotoraService();

  const layoutsDisponiveis: Layout[] = [{ id: '1', nome: 'FIC3-OPEX' }];

  const [promotoras, setPromotoras] = useState<Promotora[]>([]);
  const [promotoraSelecionada, setPromotoraSelecionada] = useState("");

  // ------------------- Funções -------------------

  const formatarData = (dataString?: string) => dataString ? new Date(dataString).toLocaleString('pt-BR') : '-';

  const carregarRemessas = async (page = 0, size = 10) => {
    setLoading(true);
    try {
      const response = await remessasService.listar(page, size);
      setRemessas(response.content ?? []);
      setPaginaAtual(response.pageable.pageNumber ?? 0);
      setTotalPaginas(response.totalPages ?? 1);
    } catch (error) {
      console.error(error);
      setAlert({ message: 'Erro ao carregar remessas', type: 'error' });
      setRemessas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarRemessas();
  }, []);

  useEffect(() => {
    if (modalUploadAberto) {
      promotoraService.listarTodasPromotorasAquisicao().then(res => {
        setPromotoras(res.data);
      });
    }
  }, [modalUploadAberto]);

  // ------------------- Upload -------------------
  const abrirSeletorArquivo = useCallback(() => fileInputRef.current?.click(), []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) setArquivoSelecionado(file);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setArquivoSelecionado(file);
  }, []);


  const enviarArquivo = async () => {
    if (!arquivoSelecionado || !layoutSelecionado || !promotoraSelecionada) {
      setAlert({ message: 'Selecione um arquivo, um layout e uma promotora antes de enviar.', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', arquivoSelecionado);
      formData.append('layout', layoutSelecionado);
      formData.append('idPromotora', promotoraSelecionada);
      formData.append('usuario', localStorage.getItem('nome') || 'USER_API');
      formData.append('codigo', campoDescritivo || '');

      const response = await remessasService.upload(formData);
      setRemessas(prev => [...prev, response]);
      setAlert({ message: 'Arquivo enviado com sucesso!', type: 'success' });

      setModalUploadAberto(false);
      setArquivoSelecionado(null);
      setLayoutSelecionado('');
      setPromotoraSelecionada('');
      setCampoDescritivo('');
    } catch (error) {
      console.error(error);
      const mensagemErro = (error as any)?.response?.data || 'Erro ao enviar o arquivo.';
      setAlert({ message: mensagemErro, type: 'error' });
    } finally {
      setLoading(false);
    }
  };


  // ------------------- Download -------------------
  const baixarArquivo = async (uuid: string, nomeArquivo: string) => {
    setLoading(true);
    try {
      const blob = await remessasService.download(uuid);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setAlert({ message: `Download iniciado: ${nomeArquivo}`, type: 'success' });
    } catch (error) {
      console.error(error);
      setAlert({ message: 'Erro ao baixar o arquivo.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Logs -------------------
  const verLogsOutrosStatus = async (remessa: Remessa) => {
    setLoading(true);
    try {
      const logs = await remessasService.listarLogsOutrosStatus(remessa.id);
      setRemessaSelecionada(remessa);
      setLogsOutrosStatus(logs);
      setModalLogsAberto(true);
    } catch (error) {
      console.error(error);
      setAlert({ message: 'Erro ao carregar logs de outros status.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const verLogsRemessa = async (idRemessa: number) => {
    setLoading(true);
    try {
      const response = await remessasService.exportarCsv(idRemessa);
      const blob = new Blob([response], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `detalhes_aquisicao_${idRemessa}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setAlert({ message: `Download do CSV da remessa ${idRemessa} iniciado com sucesso!`, type: 'success' });
    } catch (error) {
      console.error(error);
      setAlert({ message: `Erro ao processar logs da remessa ${idRemessa}.`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Desfazer -------------------
  const desfazerRemessa = async (idRemessa: number) => {
    if (!window.confirm('Deseja realmente desfazer esta remessa?')) return;

    setLoading(true);
    try {
      await remessasService.desfazer(idRemessa);
      setAlert({ message: `Remessa ${idRemessa} desfeita com sucesso!`, type: 'success' });
      carregarRemessas(paginaAtual);
    } catch (error) {
      console.error(error);
      setAlert({ message: `Erro ao desfazer a remessa ${idRemessa}.`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Paginação -------------------
  const irParaPagina = (novaPagina: number) => {
    if (novaPagina >= 0 && novaPagina < totalPaginas) {
      carregarRemessas(novaPagina);
    }
  };

  // ------------------- Render -------------------
  return (
    <div className="container-fluid mt-3">
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}

      <div className="card">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Minhas Aquisições</h5>
          <button className="btn btn-primary" onClick={() => setModalUploadAberto(true)}>
            <FontAwesomeIcon icon={faUpload} className="me-2" />Nova Aquisição
          </button>
        </div>

        <div className="card-body">
          {loading ? (
            <Spinner loading={loading} />
          ) : remessas.length === 0 ? (
            <div className="alert alert-info text-center">Nenhuma remessa encontrada</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-bordered table-hover table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Código</th>
                      <th>Nome do Arquivo</th>
                      <th>Layout</th>
                      <th>Status</th>
                      <th>Contratos Sucesso</th>
                      <th>Contratos Erro</th>
                      <th>Total Contratos</th>
                      <th>Data de Criação</th>
                      <th>Data de Processamento</th>
                      <th>Data de Finalização</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {remessas.map(remessa => (
                      <tr key={remessa.uuid}>
                        <td>{remessa.id}</td>
                        <td>{remessa.codigo}</td>
                        <td>{remessa.nomeArquivo}</td>
                        <td>{remessa.layout}</td>
                        <td>
                          <span className={`badge ${remessa.status === 'FINALIZADO' ? 'bg-success' :
                            remessa.status === 'ERRO' ? 'bg-danger' :
                              remessa.status === 'PROCESSANDO' ? 'bg-primary' : 'bg-secondary'
                            }`}>{remessa.status}</span>
                        </td>
                        <td>{remessa.contratosSucesso}</td>
                        <td>{remessa.contratosErro}</td>
                        <td>{remessa.totalContratos}</td>
                        <td>{formatarData(remessa.dtaCriacao)}</td>
                        <td>{formatarData(remessa.dtaProcessamento)}</td>
                        <td>{formatarData(remessa.dtaFinalizacao)}</td>
                        <td>
                          {remessa.status === 'FINALIZADO' && (
                            <button className="btn btn-sm btn-warning" onClick={() => desfazerRemessa(remessa.id)}>
                              <FontAwesomeIcon icon={faRotateLeft} /> Desfazer
                            </button>
                          )}
                          {remessa.status === 'FINALIZADO' && (
                            <button className="btn btn-sm btn-success ms-2" onClick={() => verLogsRemessa(remessa.id)}>
                              <FontAwesomeIcon icon={faFile} className="me-1" /> Detalhes
                            </button>
                          )}
                          <button className="btn btn-sm btn-success ms-2" onClick={() => verLogsOutrosStatus(remessa)}>
                            <FontAwesomeIcon icon={faFile} className="me-1" /> Histórico
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => irParaPagina(paginaAtual - 1)} disabled={paginaAtual === 0}>
                  <FontAwesomeIcon icon={faChevronLeft} /> Anterior
                </button>
                <span>Página {paginaAtual + 1} de {totalPaginas}</span>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => irParaPagina(paginaAtual + 1)} disabled={paginaAtual + 1 >= totalPaginas}>
                  Próxima <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal Upload */}
      {modalUploadAberto && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enviar Nova Aquisição</h5>
                <button type="button" className="btn-close" onClick={() => setModalUploadAberto(false)}></button>
              </div>
              <div className="modal-body">
                <div
                  className={`border p-3 mb-3 text-center ${isDragging ? 'bg-light' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{ cursor: 'pointer' }}
                  onClick={abrirSeletorArquivo}
                >
                  {arquivoSelecionado ? arquivoSelecionado.name : 'Arraste o arquivo ou clique para selecionar'}
                </div>
                <input type="file" className="d-none" ref={fileInputRef} onChange={handleFileChange} />

                {/* Novo Select de Promotoras */}
                <label className="form-label">Promotora de Aquisição</label>
                <select
                  className="form-select mb-3"
                  value={promotoraSelecionada}
                  onChange={e => setPromotoraSelecionada(e.target.value)}
                >
                  <option value="">Selecione a promotora</option>
                  {promotoras.map(p => (
                    <option key={p.id} value={p.id}>{p.nome}</option>
                  ))}
                </select>

                <label className="form-label">Layout</label>
                <select
                  className="form-select mb-3"
                  value={layoutSelecionado}
                  onChange={e => setLayoutSelecionado(e.target.value)}
                >
                  <option value="">Selecione o layout</option>
                  {layoutsDisponiveis.map(layout => (
                    <option key={layout.id} value={layout.nome}>{layout.nome}</option>
                  ))}
                </select>

                {/* Campo descritivo */}
                <label htmlFor="campoDescritivo" className="form-label">
                  Campo Descritivo <small className="text-muted">(opcional)</small>
                </label>
                <input
                  type="text"
                  id="campoDescritivo"
                  className="form-control mb-3"
                  value={campoDescritivo}
                  onChange={e => setCampoDescritivo(e.target.value)}
                  placeholder="Digite um texto de controle interno"
                  maxLength={50}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalUploadAberto(false)}>
                  <FontAwesomeIcon icon={faTimesCircle} className="me-2" />Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={enviarArquivo}
                  disabled={loading || !promotoraSelecionada || !layoutSelecionado || !arquivoSelecionado}
                >
                  <FontAwesomeIcon icon={faUpload} className="me-2" />Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Logs de outros status */}
      {modalLogsAberto && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Histórico</h5>
                <button type="button" className="btn-close" onClick={() => setModalLogsAberto(false)}></button>
              </div>
              <div className="modal-body">
                {loading ? <Spinner loading={loading} /> :
                  <table className="table table-bordered table-hover table-sm">
                    <thead className="table-light">
                      <tr>
                        <th>Status</th>
                        <th>Descrição</th>
                        <th>Data de Processamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          id: -1,
                          status: 'CRIAÇÃO',
                          descricao: `Criado pelo usuário ${remessaSelecionada?.usuario ?? '-'}`,
                          dtaProcessamento: formatarData(remessaSelecionada?.dtaCriacao)
                        },
                        ...logsOutrosStatus
                      ].map(log => (
                        <tr key={log.id}>
                          <td>{log.status}</td>
                          <td>{log.descricao}</td>
                          <td>{log.dtaProcessamento ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
                {logsOutrosStatus.length === 0 && !loading && <div className="alert alert-info text-center">Nenhum Histórico encontrado</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalLogsAberto(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default RemessasComponent;